import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Phone, CheckCircle, AlertCircle, Send, Shield } from 'lucide-react';
import { toast } from 'react-toastify';

const PhoneVerification = ({ onVerified, onSkip }) => {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState('phone'); // phone, code, success
  const [phone, setPhone] = useState(user?.phone || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  // Formatar telefone brasileiro
  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,2})(\d{0,5})(\d{0,4})$/);
    if (match) {
      return !match[2] ? match[1] : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;
    }
    return value;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    if (formatted.replace(/\D/g, '').length <= 11) {
      setPhone(formatted);
    }
  };

  const sendVerificationCode = async () => {
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      toast.error('Por favor, digite um número de telefone válido');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/auth/send-phone-verification', {
        phone: phone.replace(/\D/g, '')
      });
      
      setStep('code');
      setTimeLeft(600); // 10 minutos
      toast.success('Código enviado por SMS!');
      
      // Countdown timer
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Erro ao enviar SMS:', error);
      toast.error(error.response?.data?.message || 'Erro ao enviar código');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!code || code.length !== 6) {
      toast.error('Por favor, digite o código de 6 dígitos');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/auth/verify-phone', { code });
      
      setStep('success');
      updateUser(response.data.user);
      toast.success('Telefone verificado com sucesso!');
      
      setTimeout(() => {
        onVerified && onVerified();
      }, 2000);
      
    } catch (error) {
      console.error('Erro na verificação:', error);
      toast.error(error.response?.data?.message || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderStep = () => {
    switch (step) {
      case 'phone':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Phone className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verificar telefone</h2>
              <p className="text-gray-600">
                Digite seu número de telefone para receber um código de verificação por SMS.
              </p>
            </div>
            
            <div className="form-group">
              <label className="form-label">Número de telefone:</label>
              <input
                type="tel"
                className="form-input"
                placeholder="(11) 99999-9999"
                value={phone}
                onChange={handlePhoneChange}
              />
              <p className="text-xs text-gray-500 mt-1">
                Digite apenas números. Formato: (11) 99999-9999
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={sendVerificationCode}
                disabled={loading || !phone}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Enviar código
              </button>
              
              {onSkip && (
                <button
                  onClick={onSkip}
                  className="w-full text-gray-600 hover:text-gray-800 text-sm"
                >
                  Pular por agora
                </button>
              )}
            </div>
          </div>
        );

      case 'code':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Shield className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Digite o código</h2>
              <p className="text-gray-600 mb-2">
                Enviamos um código de 6 dígitos para:
              </p>
              <p className="font-semibold text-gray-900">{phone}</p>
            </div>
            
            <div className="form-group">
              <label className="form-label">Código de verificação:</label>
              <input
                type="text"
                className="form-input text-center text-2xl tracking-widest"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
              />
              {timeLeft > 0 && (
                <p className="text-xs text-gray-500 mt-1 text-center">
                  ⏰ Código expira em: {formatTime(timeLeft)}
                </p>
              )}
            </div>
            
            <div className="space-y-3">
              <button
                onClick={verifyCode}
                disabled={loading || code.length !== 6}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                Verificar código
              </button>
              
              <button
                onClick={() => setStep('phone')}
                className="w-full text-gray-600 hover:text-gray-800 text-sm"
              >
                ← Alterar número
              </button>
              
              {timeLeft === 0 && (
                <button
                  onClick={sendVerificationCode}
                  disabled={loading}
                  className="w-full text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Reenviar código
                </button>
              )}
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center space-y-6">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Telefone verificado!</h2>
              <p className="text-gray-600">
                Seu número foi verificado com sucesso. Agora você receberá notificações importantes.
              </p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 text-sm">
                ✅ Verificação concluída com sucesso!
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-blue-600 mb-2">🏛️ Igreja Apascentar</h1>
        <p className="text-gray-500">Verificação de Telefone</p>
      </div>
      
      {renderStep()}
    </div>
  );
};

export default PhoneVerification;